/**
 * Page d'inscription.
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    password: '',
    password_confirm: '',
    accepts_terms: false,
    accepts_privacy_policy: false,
    accepts_data_processing: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Rediriger vers le dashboard si l'utilisateur est déjà connecté
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.password_confirm) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    // Validation des consentements RGPD obligatoires
    if (!formData.accepts_terms || !formData.accepts_privacy_policy || !formData.accepts_data_processing) {
      setError('Vous devez accepter les conditions générales, la politique de confidentialité et le traitement des données pour créer un compte');
      return;
    }

    setLoading(true);
    const result = await register(formData);

    if (result.success) {
      navigate('/login');
    } else {
      setError(JSON.stringify(result.error));
    }

    setLoading(false);
  };

  return (
    <div className="auth-page">
      {/* Navbar similaire à la page d'accueil */}
      <nav className="auth-navbar">
        <div className="auth-nav-container">
          <Link to="/" className="auth-nav-logo">
            <img src="/images/logo_transparent.png" alt="Crions D'Couleurs" className="logo-img" />
            <span className="logo-text">Crions D'Couleurs</span>
          </Link>

          <div className="auth-nav-menu">
            <Link to="/" className="auth-nav-link">Accueil</Link>
            <Link to="/sejours" className="auth-nav-link">Séjours</Link>
            <Link to="/login" className="auth-nav-btn">Se connecter</Link>
          </div>
        </div>
      </nav>

      {/* Conteneur principal avec formulaire */}
      <div className="auth-container">
        <div className="auth-card" style={{ maxWidth: '600px' }}>
          <div className="auth-header">
            <h1 className="auth-title">Créer un compte</h1>
            <p className="auth-subtitle">Rejoignez-nous pour inscrire vos enfants à nos séjours</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {error && (
              <div className="auth-error">
                {error}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Adresse email <span className="required">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="form-input"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="first_name" className="form-label">
                  Prénom <span className="required">*</span>
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  className="form-input"
                  placeholder="Jean"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="last_name" className="form-label">
                  Nom <span className="required">*</span>
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  className="form-input"
                  placeholder="Dupont"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Téléphone
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                className="form-input"
                placeholder="06 12 34 56 78"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="address" className="form-label">
                Adresse
              </label>
              <textarea
                id="address"
                name="address"
                className="form-input"
                placeholder="Votre adresse complète"
                rows="3"
                value={formData.address}
                onChange={handleChange}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Mot de passe <span className="required">*</span>
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="form-input"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password_confirm" className="form-label">
                  Confirmer <span className="required">*</span>
                </label>
                <input
                  id="password_confirm"
                  name="password_confirm"
                  type="password"
                  required
                  className="form-input"
                  placeholder="••••••••"
                  value={formData.password_confirm}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Section RGPD */}
            <div className="rgpd-section">
              <p className="rgpd-title">Consentements RGPD</p>

              <div className="checkbox-group">
                <input
                  id="accepts_terms"
                  name="accepts_terms"
                  type="checkbox"
                  checked={formData.accepts_terms}
                  onChange={handleChange}
                  className="form-checkbox"
                />
                <label htmlFor="accepts_terms" className="checkbox-label">
                  J'accepte les <Link to="/cgv" className="form-link" target="_blank">conditions générales d'utilisation</Link> <span className="required">*</span>
                </label>
              </div>

              <div className="checkbox-group">
                <input
                  id="accepts_privacy_policy"
                  name="accepts_privacy_policy"
                  type="checkbox"
                  checked={formData.accepts_privacy_policy}
                  onChange={handleChange}
                  className="form-checkbox"
                />
                <label htmlFor="accepts_privacy_policy" className="checkbox-label">
                  J'accepte la <Link to="/politique-confidentialite" className="form-link" target="_blank">politique de confidentialité</Link> <span className="required">*</span>
                </label>
              </div>

              <div className="checkbox-group">
                <input
                  id="accepts_data_processing"
                  name="accepts_data_processing"
                  type="checkbox"
                  checked={formData.accepts_data_processing}
                  onChange={handleChange}
                  className="form-checkbox"
                />
                <label htmlFor="accepts_data_processing" className="checkbox-label">
                  J'accepte le traitement de mes données personnelles <span className="required">*</span>
                </label>
              </div>

              <p className="rgpd-note">
                Les champs marqués d'un <span className="required">*</span> sont obligatoires
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="auth-button"
            >
              {loading ? 'Inscription...' : 'S\'inscrire'}
            </button>

            <div className="auth-footer">
              <p className="auth-footer-text">
                Déjà un compte ?{' '}
                <Link to="/login" className="auth-footer-link">
                  Se connecter
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
